import React, { useState, useEffect, useCallback, useRef } from 'react';
import s from './OfficeTour.module.css';

// ─── OFFICE IMAGES ────────────────────────────────────────────────────────────
// Replace these Unsplash placeholders with your actual office photos.
// Each object: { src, thumb, caption, area }
const OFFICE_IMAGES = [
  {
    src:   'https://images.unsplash.com/photo-1629909615184-74f495363b67?w=1400&q=90',
    thumb: 'https://images.unsplash.com/photo-1629909615184-74f495363b67?w=480&q=75',
    caption: 'Modern Reception & Waiting Area',
    area:    'Reception',
  },
  {
    src:   'https://images.unsplash.com/photo-1588776814546-1ffbb93a5289?w=1400&q=90',
    thumb: 'https://images.unsplash.com/photo-1588776814546-1ffbb93a5289?w=480&q=75',
    caption: 'State-of-the-Art Treatment Room',
    area:    'Treatment',
  },
  {
    src:   'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=1400&q=90',
    thumb: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=480&q=75',
    caption: 'Advanced Digital Imaging Suite',
    area:    'Technology',
  },
  {
    src:   'https://images.unsplash.com/photo-1607613009820-a29f7bb81c04?w=1400&q=90',
    thumb: 'https://images.unsplash.com/photo-1607613009820-a29f7bb81c04?w=480&q=75',
    caption: 'Comfortable Consultation Room',
    area:    'Consultation',
  },
  {
    src:   'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=1400&q=90',
    thumb: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=480&q=75',
    caption: 'Sterilization & Safety Center',
    area:    'Safety',
  },
  {
    src:   'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=1400&q=90',
    thumb: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=480&q=75',
    caption: 'Panoramic X-Ray Technology',
    area:    'Technology',
  },
  {
    src:   'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1400&q=90',
    thumb: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=480&q=75',
    caption: 'Patient-Friendly Recovery Area',
    area:    'Treatment',
  },
  {
    src:   'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=1400&q=90',
    thumb: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=480&q=75',
    caption: 'Bright, Welcoming Corridors',
    area:    'Reception',
  },
];

// ─── OFFICE VIDEO ─────────────────────────────────────────────────────────────
// Replace with your actual YouTube/Vimeo embed ID.
// YouTube: https://www.youtube.com/watch?v=XXXXXXXXXXX  →  videoId = 'XXXXXXXXXXX'
// Vimeo:   https://vimeo.com/XXXXXXXXX                  →  type = 'vimeo', videoId = 'XXXXXXXXX'
const TOUR_VIDEO = {
  type:    'youtube',           // 'youtube' | 'vimeo'
  videoId: 'U64z9HgAZdo',      // Replace with your video ID
  title:   'Celebrity Smile Studio — Virtual Office Tour',
  poster:  'https://images.unsplash.com/photo-1629909615184-74f495363b67?w=1200&q=80',
};

// ─── Filter tags ──────────────────────────────────────────────────────────────
const AREAS = ['All', ...Array.from(new Set(OFFICE_IMAGES.map(i => i.area)))];

// ─── Lightbox ─────────────────────────────────────────────────────────────────
function Lightbox({ images, index, onClose, onNav }) {
  const img = images[index];

  // Keyboard nav & close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape')     onClose();
      if (e.key === 'ArrowRight') onNav(1);
      if (e.key === 'ArrowLeft')  onNav(-1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onNav]);

  return (
    <div className={s.lightboxOverlay} onClick={onClose} role="dialog" aria-modal="true">
      {/* Main image — stop propagation so clicking image doesn't close */}
      <div className={s.lightboxContent} onClick={e => e.stopPropagation()}>
        <img src={img.src} alt={img.caption} className={s.lightboxImg} />

        <div className={s.lightboxCaption}>
          <span className={s.lightboxArea}>{img.area}</span>
          <span className={s.lightboxTitle}>{img.caption}</span>
          <span className={s.lightboxCount}>{index + 1} / {images.length}</span>
        </div>

        {/* Prev / Next */}
        <button className={`${s.navBtn} ${s.navPrev}`} onClick={() => onNav(-1)} aria-label="Previous">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="m15 18-6-6 6-6"/>
          </svg>
        </button>
        <button className={`${s.navBtn} ${s.navNext}`} onClick={() => onNav(1)} aria-label="Next">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="m9 18 6-6-6-6"/>
          </svg>
        </button>

        {/* Thumbnail strip */}
        <div className={s.thumbStrip}>
          {images.map((im, i) => (
            <button key={i} className={`${s.thumbBtn} ${i === index ? s.thumbActive : ''}`}
              onClick={() => onNav(i - index)} aria-label={im.caption}>
              <img src={im.thumb} alt={im.caption} />
            </button>
          ))}
        </div>
      </div>

      {/* Close */}
      <button className={s.closeBtn} onClick={onClose} aria-label="Close">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M18 6 6 18M6 6l12 12"/>
        </svg>
      </button>
    </div>
  );
}

// ─── Video player ─────────────────────────────────────────────────────────────
function VideoPlayer({ video }) {
  const [playing, setPlaying] = useState(false);

  const embedSrc = video.type === 'youtube'
    ? `https://www.youtube.com/embed/${video.videoId}?autoplay=1&rel=0&modestbranding=1`
    : `https://player.vimeo.com/video/${video.videoId}?autoplay=1`;

  return (
    <div className={s.videoSection}>
      <div className={s.videoLabel}>
        <span className={s.videoBadge}>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="m5 3 14 9-14 9V3z"/></svg>
          Virtual Tour
        </span>
        <h3 className={s.videoTitle}>{video.title}</h3>
        <p className={s.videoSubtitle}>Take a walkthrough of our modern, welcoming office before your visit.</p>
      </div>

      <div className={s.videoWrap}>
        {playing ? (
          <iframe
            src={embedSrc}
            title={video.title}
            className={s.videoFrame}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <div className={s.videoPoster} onClick={() => setPlaying(true)}>
            <img src={video.poster} alt="Office tour video thumbnail" className={s.posterImg} />
            <div className={s.posterOverlay} />
            <button className={s.playBtn} aria-label="Play office tour video">
              <div className={s.playBtnInner}>
                <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
                  <path d="m5 3 14 9-14 9V3z"/>
                </svg>
              </div>
              <div className={s.playRipple} />
            </button>
            <div className={s.posterCaption}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m5 3 14 9-14 9V3z"/>
              </svg>
              Click to play
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function OfficeTour() {
  const [activeArea, setActiveArea]   = useState('All');
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const filtered = activeArea === 'All'
    ? OFFICE_IMAGES
    : OFFICE_IMAGES.filter(i => i.area === activeArea);

  const openLightbox = (idx) => setLightboxIdx(idx);
  const closeLightbox = useCallback(() => setLightboxIdx(null), []);
  const navLightbox = useCallback((delta) => {
    setLightboxIdx(prev => {
      const next = prev + delta;
      if (next < 0) return filtered.length - 1;
      if (next >= filtered.length) return 0;
      return next;
    });
  }, [filtered.length]);

  return (
    <div className={s.wrap}>

      {/* ── Video at top ─────────────────────────────────── */}
      <VideoPlayer video={TOUR_VIDEO} />

      {/* ── Gallery header ───────────────────────────────── */}
      <div className={s.galleryHeader}>
        <div>
          <h2 className={s.galleryTitle}>Photo Gallery</h2>
          <p className={s.gallerySubtitle}>Explore our modern, welcoming facility in Flourtown</p>
        </div>
        {/* Area filter chips */}
        <div className={s.areaFilters}>
          {AREAS.map(area => (
            <button key={area}
              className={`${s.areaChip} ${activeArea === area ? s.areaChipActive : ''}`}
              onClick={() => setActiveArea(area)}>
              {area}
            </button>
          ))}
        </div>
      </div>

      {/* ── Masonry-style grid ───────────────────────────── */}
      <div className={s.grid}>
        {filtered.map((img, i) => (
          <button
            key={img.src}
            className={`${s.cell} ${i === 0 ? s.cellFeatured : ''}`}
            onClick={() => openLightbox(i)}
            aria-label={`View: ${img.caption}`}
          >
            <img src={img.thumb} alt={img.caption} className={s.cellImg} loading="lazy" />
            <div className={s.cellOverlay}>
              <span className={s.cellArea}>{img.area}</span>
              <span className={s.cellCaption}>{img.caption}</span>
              <span className={s.cellZoom}>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35M11 8v6M8 11h6"/>
                </svg>
              </span>
            </div>
          </button>
        ))}
      </div>

      <p className={s.galleryNote}>
        {filtered.length} photo{filtered.length !== 1 ? 's' : ''} · Click any image to zoom · Use arrow keys to navigate
      </p>

      {/* ── Lightbox ─────────────────────────────────────── */}
      {lightboxIdx !== null && (
        <Lightbox
          images={filtered}
          index={lightboxIdx}
          onClose={closeLightbox}
          onNav={navLightbox}
        />
      )}
    </div>
  );
}
